import { PageSkeleton } from "../../components/brand/Experience";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, Gamepad2 } from "lucide-react";
import { toast } from "sonner";
import { api, playState, MODE_FR } from "../../lib/api";
import { PRIORITY } from "../../lib/labels";
import { useCatalog, usePatient } from "../../hooks/useData";
import { Checkbox } from "../../components/ui/checkbox";
import { Slider } from "../../components/ui/slider";
import { PageHeader, Panel } from "./CareLayout";
import { PROJECTILE_FR } from "../../components/game/Projectile";

const MODE_DESC = {
  assisted: "Cibles confortablement dans la zone de mouvement accessible (50–75 % de la limite sûre).",
  rehabilitation: "Cibles qui s'approchent progressivement de la limite fonctionnelle sûre (62–92 %).",
  challenge: "Cibles plus exigeantes (80–100 %) sans jamais dépasser les limites définies.",
};

export default function SessionConfig() {
  const { pid } = useParams();
  const nav = useNavigate();
  const { data: p } = usePatient(pid);
  const { data: catalog } = useCatalog();
  const [mode, setMode] = useState("rehabilitation");
  const [duration, setDuration] = useState(20);
  const [priority, setPriority] = useState("right_upper_limb");
  const [objectives, setObjectives] = useState(["shoulder_flexion", "functional_reach", "trunk_control", "bilateral_coordination"]);
  const [missions, setMissions] = useState(null);
  const [projectile, setProjectile] = useState("sling");
  const [legShare, setLegShare] = useState(60);
  const [goals, setGoals] = useState(null);
  const [created, setCreated] = useState(null);
  if (!p || !catalog) return <PageSkeleton />;
  const forbidden = p.restrictions.forbidden_movements;
  const village = catalog.missions.filter((m) => m.level === "village");
  const selected = missions ?? village.filter((m) => !forbidden.includes(m.movement)).map((m) => m.key);
  const maxDur = p.restrictions.max_session_duration_min;
  const aff = p.clinical_profile.affected_side;
  const legGoals = goals ?? { left: aff === "left" ? 50 : 70, right: aff === "right" ? 50 : 70 };
  const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const create = async () => {
    if (!selected.length) return toast.error("Sélectionnez au moins une mission");
    const { data } = await api.post("/sessions", { patient_id: pid, config: { level_key: "village", mode, duration_min: Math.min(duration, maxDur), priority, objectives, mission_keys: selected, projectile, affected_leg_share: legShare / 100, leg_goals: legGoals } });
    setCreated(data);
    toast.success("Séance démarrée — prête en mode patient");
  };
  const toPatient = () => { playState.set({ patientId: pid, sessionId: created.id }); nav("/patient"); };

  if (created) return (
    <div className="mx-auto max-w-xl rounded-3xl bg-[#0A2540] p-8 text-center text-white" data-testid="session-created-panel">
      <CheckCircle2 className="mx-auto h-16 w-16 text-[#17A2A2]" />
      <h2 className="mt-4 font-display text-3xl font-extrabold">Séance prête</h2>
      <p className="mt-2 text-white/70">{p.name} · Le Village · {MODE_FR[created.config.mode]} · {created.config.duration_min} min · {selected.length} missions</p>
      <button data-testid="switch-to-patient-button" onClick={toPatient} className="mt-6 flex h-16 w-full items-center justify-center gap-2 rounded-full bg-brand text-lg font-extrabold"><Gamepad2 />Passer en mode patient</button>
      <button data-testid="back-to-patient-button" onClick={() => nav(`/care/patients/${pid}`)} className="mt-3 h-12 text-sm font-semibold text-white/70">Retour au dossier</button>
    </div>
  );

  return (
    <div data-testid="session-config">
      <PageHeader title="Configurer la séance" sub={`${p.name} · ${p.clinical_profile.diagnosis_label} · Côté atteint ${p.clinical_profile.affected_side === "right" ? "droit" : p.clinical_profile.affected_side === "left" ? "gauche" : "bilatéral"}`}>
        <button data-testid="start-session-button" onClick={create} className="h-11 rounded-xl bg-brand px-5 text-sm font-bold text-white">Démarrer la séance</button>
      </PageHeader>
      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title="Mode de difficulté adaptative">
          <div className="space-y-2">
            {catalog.modes.map((m) => (
              <button key={m} data-testid={`mode-${m}`} onClick={() => setMode(m)}
                className={`w-full rounded-xl border-2 p-4 text-left transition-colors ${mode === m ? "border-[#17A2A2] bg-primary/10" : "border-border hover:border-slate-300"}`}>
                <p className="font-bold">{MODE_FR[m]}</p><p className="text-sm text-muted-foreground">{MODE_DESC[m]}</p>
              </button>
            ))}
          </div>
        </Panel>
        <Panel title="Paramètres de séance">
          <p className="mb-2 text-sm font-semibold">Durée : <span data-testid="duration-value">{Math.min(duration, maxDur)}</span> min <span className="text-xs text-muted-foreground/80">(max {maxDur} min)</span></p>
          <Slider data-testid="duration-slider" min={5} max={maxDur} step={1} value={[Math.min(duration, maxDur)]} onValueChange={([v]) => setDuration(v)} />
          <p className="mb-2 mt-6 text-sm font-semibold">Priorité</p>
          <select data-testid="priority-select" className="h-11 w-full rounded-xl border border-border px-3 text-sm" value={priority} onChange={(e) => setPriority(e.target.value)}>
            {Object.entries(PRIORITY).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <p className="mb-2 mt-6 text-sm font-semibold">Mode de jeu des cibles d'atteinte</p>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(PROJECTILE_FR).map(([k, l]) => (
              <button key={k} data-testid={`projectile-${k}`} onClick={() => setProjectile(k)}
                className={`h-11 rounded-xl border-2 text-sm font-semibold ${projectile === k ? "border-[#17A2A2] bg-primary/10" : "border-border"}`}>{l}</button>
            ))}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">La visée correspond à la position d'atteinte ; maintien stable 0,5 s requis avant le tir (contrôle, pas vitesse).</p>
          <p className="mb-2 mt-6 text-sm font-semibold">Objectifs</p>
          <div className="grid grid-cols-2 gap-2">
            {catalog.objectives.map((o) => (
              <label key={o.key} className="flex h-11 items-center gap-2 rounded-xl border border-border px-3 text-sm">
                <Checkbox data-testid={`objective-${o.key}`} checked={objectives.includes(o.key)} onCheckedChange={() => setObjectives(toggle(objectives, o.key))} />{o.fr}</label>
            ))}
          </div>
        </Panel>
        <Panel title="Membres inférieurs · Niveau Savane" className="lg:col-span-2" testId="leg-share-panel">
          <div className="grid gap-6 md:grid-cols-[1fr_1.4fr] md:items-center">
            <div className="rounded-2xl bg-muted p-4">
              <p className="text-xs text-muted-foreground">Jambe atteinte (fiche patient)</p>
              <p className="font-display text-2xl font-semibold" data-testid="leg-affected-side">{{ right: "Droite", left: "Gauche" }[p.clinical_profile.affected_side] || "Bilatérale"}</p>
              <p className="mt-1 text-xs text-muted-foreground">Modifiable dans le dossier patient › Modifier.</p>
            </div>
            <div>
              <p className="mb-2 text-sm font-semibold">Part des pas sur la jambe atteinte : <span data-testid="leg-share-value">{legShare}</span> %</p>
              <Slider data-testid="leg-share-slider" min={50} max={80} step={5} value={[legShare]} onValueChange={([v]) => setLegShare(v)} />
              <div className="mt-1 flex justify-between text-[11px] text-muted-foreground"><span>50 % · alterné</span><span>80 % · intensif</span></div>
              <div className="mt-5 grid gap-4 sm:grid-cols-2" data-testid="leg-goals">
                {["left", "right"].map((k) => (
                  <div key={k}>
                    <p className="mb-2 text-sm font-semibold">Objectif jambe {k === "left" ? "gauche" : "droite"}{p.clinical_profile.affected_side === k ? " (atteinte)" : ""} : <span data-testid={`leg-goal-${k}-value`}>{legGoals[k]}</span> %</p>
                    <Slider data-testid={`leg-goal-${k}-slider`} min={30} max={90} step={5} value={[legGoals[k]]} onValueChange={([v]) => setGoals({ ...legGoals, [k]: v })} />
                  </div>
                ))}
              </div>
              <p className="mt-2 text-[11px] text-muted-foreground">Score cible d'amplitude (en % de la zone sûre). Le patient voit une barre d'objectif douce dans la Savane et la Rivière, sans pénalité.</p>
              <p className="mt-3 text-xs text-muted-foreground">Les pas sur la jambe atteinte sont répartis régulièrement (jamais en série). Amplitude bornée à la zone sûre, aucun chrono, cible adaptée en douceur après chaque essai.</p>
            </div>
          </div>
        </Panel>
        <Panel title="Missions du Village (sélection automatique selon le profil)" className="lg:col-span-2">
          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
            {village.map((m) => {
              const blocked = forbidden.includes(m.movement);
              return (
                <label key={m.key} className={`flex items-start gap-3 rounded-xl border border-border p-4 ${blocked ? "opacity-50" : ""}`}>
                  <Checkbox data-testid={`mission-toggle-${m.key}`} disabled={blocked} checked={selected.includes(m.key)} onCheckedChange={() => setMissions(toggle(selected, m.key))} />
                  <span><b className="block text-sm">{m.order}. {m.name_fr}</b><span className="text-xs text-muted-foreground">{m.movement_fr} · {m.reps} rép.{blocked && " · interdit par restriction"}</span></span>
                </label>
              );
            })}
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            Ciblage : ~{Math.round(catalog.affected_side_ratio * 100)} % des cibles unilatérales sur le côté atteint, mouvements bilatéraux inclus, amplitudes bornées par les limites sûres du patient.
          </p>
        </Panel>
      </div>
    </div>
  );
}
